
import Vue from "vue";
import { format } from "date-fns";
import { get } from "lodash";
import { getOrigin } from "@/helpers";
import { userMapActions } from "@/store/modules/user";
import { formatQuoteUpdateText } from "@/helpers/quoteActivitiesFormatter";
import { IPolicyInfo } from "@/helpers/storageFormBaseInterface";
import { formatDate } from "@/helpers/dateProcessor";

export interface ActivitiesTimelineMixinType {
  generateRegularActivityString(content: string, update: any): string;
  setReadableUpdateProps(contentHtml: string): void;
  generateReadableGeneric(config: {
    content: string;
    icon: string;
    type?: string;
    link?: any;
  }): void;
  generateReadableFailGeneric(config: {
    content: string;
    icon: string;
    link?: any;
    extraData?: Record<string, any>;
  }): void;
  accountTypeFormatted: any;
}

const ActivitiesTimelineMixin: any = Vue.extend({
  props: {
    activity: {
      type: Object,
      required: true
    }
  },
  components: {
    TabMenu: () => import("@/components/TabMenu/TabMenu.vue"),
    ActivitiesView: () =>
      import("@/components/ActivitiesView/ActivitiesView.vue"),
    ActivitiesTimelineItem: () =>
      import("@/components/ActivitiesView/ActivitiesTimelineItem.vue")
  },
  data() {
    return {
      content: "",
      icon: "",
      type: "",
      link: {},
      color: "",
      size: "large",
      linkColor: "#409EFF",
      linkColorDark: "#034b9d"
    };
  },
  methods: {
    ...userMapActions(["getUser"]),

    displayChangeRequestData(policyInfo: IPolicyInfo) {
      return `<div>
        <p>Effective Date: ${formatDate(policyInfo?.effectiveDate)}</p>
        <p>Policy Number: ${policyInfo?.policyNumber}</p>
        <p>Policy Prefix: ${policyInfo?.policyPrefix}</p>
        <p>Company Number: ${policyInfo?.companyNum}</p>
        <p>Agency Name: ${policyInfo?.agencyName}</p>
        <p>Agency Code: ${policyInfo?.agencyCode}</p>
      </div>`;
    },

    formatDate(dateStr: string) {
      return format(new Date(dateStr), "MMMM d, y");
    },

    baseLink(userId: string) {
      return `${getOrigin()}/admin/users/${userId}/edit`;
    },

    generateReadableGeneric({
      content,
      icon,
      type = "info",
      link
    }: {
      content: string;
      icon: string;
      type?: string;
      link?: any;
    }) {
      this.content = `{userName} ${content}`;
      this.icon = icon;
      this.type = type;
      this.link = link || this.accountTypeFormatted;
    },

    generateReadableFailGeneric({
      content,
      icon,
      link,
      extraData = {}
    }: {
      content: string;
      icon: string;
      link?: any;
      extraData?: Record<string, any>;
    }) {
      this.content = `<p>{userName} ${content}</p><p>Reason: ${this
        .failureReason || "N/A"}</p>`;

      for (const key in extraData) {
        this.content += `<p>${key}: ${extraData[key]}</p>`;
      }

      this.icon = icon;
      this.type = "danger";
      this.link = link || this.accountTypeFormatted;
    },

    generateReadableUpdate() {
      let contentHtml = ``;
      const updatesArray = this.updates || [];

      for (let update of updatesArray) {
        if (update.field.includes("attachments")) {
          contentHtml += `<p>{userName} uploaded an attachment</p>`;
        } else {
          contentHtml = this.generateRegularActivityString(contentHtml, update);
        }
      }

      this.setReadableUpdateProps(contentHtml);
    },

    setReadableUpdateProps(content: string) {
      this.content = content;
      this.icon = "el-icon-edit";
      this.link = this.accountTypeFormatted;
    },

    generateRegularActivityString(
      contentHtml: string,
      update: any,
      userLink?: string
    ) {
      const output = formatQuoteUpdateText({
        linkColor: this.linkColor,
        activity: this.activity,
        field: update.field.replace("data.", ""),
        requiredData: update.update ?? "N/A",
        userLink
      });

      return contentHtml + output;
    }
  },

  computed: {
    formattedActivityText(): string {
      let content = this.content;
      const linksArray = Object.keys(this.link || {});

      for (const linkKey of linksArray) {
        const link = (this.link as any)[linkKey];
        if (!link) continue;

        const { name, href } = link;
        let linkNode = name
          ? href
            ? `<a style="color: ${this.linkColor}" href="${href}">${name}</a>`
            : `<span>${name}</span>`
          : "";

        content = content.replace(new RegExp(`{${linkKey}}`, "g"), linkNode);
      }

      return content;
    },

    data(): any {
      return this.activity?.data || {};
    },

    updates(): any[] {
      return get(this.data, "updates", []);
    },

    deletes(): any[] {
      return get(this.data, "deletes", []);
    },

    failureReason(): string {
      return get(this.data, "reason", "");
    },

    accountTypeFormatted(): Record<string, { name: string; href: string }> {
      let nameToDisplay = "";

      const currentUser = this.$getCurrentUser;

      if (currentUser?._id === this.activity?.userId) {
        nameToDisplay = "You";
      } else {
        nameToDisplay =
          get(this.activity.userData, "fullName") ||
          `${get(this.activity.userData, "firstName", "")} ${get(
            this.activity.userData,
            "lastName",
            ""
          )}`.trim();
      }

      return {
        userName: {
          name: nameToDisplay,
          href: this.baseLink(this.activity.userId)
        }
      };
    }
  }
});

export default ActivitiesTimelineMixin;
